import { bytesToHex } from '@stacks/common';
import {
  createStacksPublicKey,
  deserializeTransaction,
  serializePayload,
  StacksTransaction,
} from '@stacks/transactions';
import BigNumber from 'bignumber.js';

import { Money, StacksFeeEstimate, StacksTxFeeEstimation } from '../types/api/ApiResponseTypes';

import useNetwork from './use-network';
import useSafe from './use-safe';
// import {useEffect} from "react";

type NumType = BigNumber | bigint | number;

function createMoney(value: NumType, symbol: string): Money {
  const decimals = 6;
  const amount = new BigNumber(value.toString());

  return Object.freeze({ amount, symbol, decimals });
}

interface ParseStacksTxFeeEstimationResponseArgs {
  feeEstimation: StacksTxFeeEstimation;
  maxValues?: Money[];
  minValues?: Money[];
  txByteLength: number | null;
}

enum FeeCalculationTypes {
  Api = 'api',
  Default = 'default',
  DefaultSimulated = 'default-simulated',
  FeesCapped = 'fees-capped',
}

interface Fees {
  blockchain: string;
  estimates: StacksFeeEstimate[];
  calculation: string;
}

const DEFAULT_FEE_RATE = 400;
const marginFromDefaultFeeDecimalPercent = 0.1;

// TODO_R: clean this hook
// TODO_R: move into SafeDetailsPage/hooks
export const useCountTxFee = () => {
  const { safe } = useSafe();
  const [, stacksNetwork] = useNetwork();

  function feeEstimationQueryFailedSilently(feeEstimation: StacksTxFeeEstimation) {
    return feeEstimation && (!!feeEstimation.error || !feeEstimation.estimations.length);
  }

  function calculateFeeFromFeeRate(txBytes: number, feeRate: number) {
    return new BigNumber(txBytes).multipliedBy(feeRate);
  }

  function getDefaultSimulatedFeeEstimations(estimatedByteLength: number): StacksFeeEstimate[] {
    const fee = calculateFeeFromFeeRate(estimatedByteLength, DEFAULT_FEE_RATE);

    return [
      {
        fee: createMoney(fee.multipliedBy(1 - marginFromDefaultFeeDecimalPercent), 'STX'),
        feeRate: 0,
      },
      { fee: createMoney(fee, 'STX'), feeRate: 0 },
      {
        fee: createMoney(fee.multipliedBy(1 + marginFromDefaultFeeDecimalPercent), 'STX'),
        feeRate: 0,
      },
    ];
  }

  function getFeeEstimationsWithCappedValues(
    feeEstimations: StacksFeeEstimate[],
    feeEstimationsMaxValues: Money[] | undefined,
    feeEstimationsMinValues: Money[] | undefined
  ) {
    return feeEstimations.map((feeEstimation, index) => {
      if (
        feeEstimationsMaxValues &&
        feeEstimation.fee.amount.isGreaterThan(feeEstimationsMaxValues[index].amount)
      ) {
        return { fee: feeEstimationsMaxValues[index], feeRate: 0 };
      } else if (
        feeEstimationsMinValues &&
        feeEstimation.fee.amount.isLessThan(feeEstimationsMinValues[index].amount)
      ) {
        return { fee: feeEstimationsMinValues[index], feeRate: 0 };
      } else {
        return feeEstimation;
      }
    });
  }

  function parseStacksTxFeeEstimationResponse({
    feeEstimation,
    maxValues,
    minValues,
    txByteLength,
  }: ParseStacksTxFeeEstimationResponseArgs): Fees {
    if (!!feeEstimation.error) return defaultStacksFees;
    if (txByteLength && feeEstimationQueryFailedSilently(feeEstimation)) {
      return {
        blockchain: 'stacks',
        estimates: getDefaultSimulatedFeeEstimations(txByteLength),
        calculation: FeeCalculationTypes.DefaultSimulated,
      };
    }

    const stacksFeeEstimates: StacksFeeEstimate[] = feeEstimation.estimations.map((estimate) => {
      return {
        fee: createMoney(estimate.fee, 'STX'),
        feeRate: estimate.fee_rate,
      };
    });

    if (feeEstimation.estimations && feeEstimation.estimations.length) {
      const feeEstimationsWithCappedValues = getFeeEstimationsWithCappedValues(
        stacksFeeEstimates,
        maxValues,
        minValues
      );

      return {
        blockchain: 'stacks',
        estimates: feeEstimationsWithCappedValues,
        calculation: FeeCalculationTypes.FeesCapped,
      };
    }

    return {
      blockchain: 'stacks',
      estimates: stacksFeeEstimates ?? [],
      calculation: FeeCalculationTypes.Api,
    };
  }

  const defaultFeesMinValues = [
    createMoney(2500, 'STX'),
    createMoney(3000, 'STX'),
    createMoney(3500, 'STX'),
  ];

  const feeEstimationsMaxValues = [
    createMoney(500000, 'STX'),
    createMoney(750000, 'STX'),
    createMoney(2000000, 'STX'),
  ];
  // const feeEstimationsMinValues = [
  //     createMoney(9500, 'STX'),
  //     createMoney(10000, 'STX'),
  //     createMoney(15000, 'STX'),
  // ];
  const feeEstimationsMinValues = [
    createMoney(2500, 'STX'),
    createMoney(3000, 'STX'),
    createMoney(3500, 'STX'),
  ];

  const defaultStacksFeeEstimates: StacksFeeEstimate[] = [
    { fee: defaultFeesMinValues[0], feeRate: 0 },
    { fee: defaultFeesMinValues[1], feeRate: 0 },
    { fee: defaultFeesMinValues[2], feeRate: 0 },
  ];

  const defaultStacksFees: Fees = {
    blockchain: 'stacks',
    estimates: defaultStacksFeeEstimates,
    calculation: FeeCalculationTypes.Default,
  };

  const getTxByteLength = (transaction: StacksTransaction) => {
    return transaction.serialize().byteLength;
  };

  const getTxPayload = (transaction: StacksTransaction) => {
    return bytesToHex(serializePayload(transaction.payload));
  };

  const countTxGas = async (txHash: string) => {
    const OWNER_SIGN_WEIGHT = 66;
    const transaction = deserializeTransaction(txHash);

    safe.owners.slice(0, safe.threshold - 1).forEach((o, index) => {
      transaction.appendPubkey(createStacksPublicKey(o.publicKey));
    });
    try {
      const response = await fetch(`${stacksNetwork.coreApiUrl}/v2/fees/transaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_payload: getTxPayload(transaction),
        }),
      });
      const data = await response.json();
      const txByteLength = getTxByteLength(transaction) + OWNER_SIGN_WEIGHT * safe.threshold;
      if (data.estimations) {
        return parseStacksTxFeeEstimationResponse({
          feeEstimation: data,
          maxValues: feeEstimationsMaxValues,
          minValues: feeEstimationsMinValues,
          txByteLength,
        });
      }

      return defaultStacksFees;
    } catch (error) {
      console.error('Error fetching fee:', error);

      return defaultStacksFees;
    }
    // return defaultStacksFees;
    // return {
    //     estimations: [],
    //     reason: `Fetching fee error ${data.reason}`,
    // }
  };

  return { countTxGas };
};
